import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import SkillsInput from '../../relevanter/SkillsInput';
import TagsInput from '../../relevanter/TagsInput';
import { FiltersState } from '../../hooks/useFilters';
import { ResponsesFiltersState, RESPONSES_EXPERIENCE_OPTIONS } from '../../hooks/useResponsesFilters';
import { useCurrencies } from '../../hooks/useCurrencies';
import CustomCheckbox from '../ui/CustomCheckbox';
import { formatNumberWithSpaces, parseFormattedNumber } from '../../utils/numberFormat';
import {
  buildAvitoExperienceInputPatch,
  normalizeAvitoExperienceValue,
} from '../../sources/avito/experienceRange';

// Опции для выпадающих списков
const INDUSTRY_OPTIONS = [
  { value: '', label: 'Выберите отрасль...' },
  { value: 'it', label: 'IT' },
  { value: 'finance', label: 'Финансы' },
  { value: 'marketing', label: 'Маркетинг' },
  { value: 'sales', label: 'Продажи' },
  { value: 'hr', label: 'HR' },
  { value: 'education', label: 'Образование' },
  { value: 'healthcare', label: 'Здравоохранение' },
  { value: 'manufacturing', label: 'Производство' },
  { value: 'retail', label: 'Розничная торговля' },
];

const SPECIALIZATION_OPTIONS = [
  { value: '', label: 'Выберите специализацию...' },
  { value: 'frontend', label: 'Frontend разработка' },
  { value: 'backend', label: 'Backend разработка' },
  { value: 'fullstack', label: 'Fullstack разработка' },
  { value: 'mobile', label: 'Мобильная разработка' },
  { value: 'devops', label: 'DevOps' },
  { value: 'data', label: 'Data Science' },
  { value: 'qa', label: 'QA/Тестирование' },
  { value: 'design', label: 'UI/UX дизайн' },
  { value: 'product', label: 'Product Management' },
];

const EXPERIENCE_OPTIONS = [
  { value: '', label: 'Выберите опыт...' },
  { value: 'no-experience', label: 'Без опыта' },
  { value: '1-3', label: '1-3 года' },
  { value: '3-5', label: '3-5 лет' },
  { value: '5-10', label: '5-10 лет' },
  { value: '10+', label: 'Более 10 лет' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: '', label: 'Любой' },
  { value: 'full', label: 'Полная занятость' },
  { value: 'part', label: 'Частичная занятость' },
  { value: 'project', label: 'Проектная работа' },
  { value: 'volunteer', label: 'Волонтерство' },
  { value: 'probation', label: 'Стажировка' }
];

const cities = [
  'Москва','Санкт-Петербург','Новосибирск','Екатеринбург','Нижний Новгород','Казань','Челябинск','Омск','Самара','Ростов-на-Дону','Уфа','Красноярск','Воронеж','Пермь'
];


// Custom Select Component
const CustomSelect: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}> = ({ label, value, onChange, options, placeholder = 'Выберите...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  const displayValue = options.find(opt => opt.value === value)?.label || placeholder;

  return (
    <div className="content-stretch flex flex-col gap-[4px] items-center relative shrink-0 w-full" ref={dropdownRef}>
      <div className="content-stretch flex gap-px items-center relative shrink-0 w-full">
        <div className="flex flex-col font-['Inter:Regular',_sans-serif] font-normal justify-center leading-[0] not-italic relative shrink-0 text-[14px] text-black text-nowrap tracking-[-0.084px]">
          <p className="leading-[20px] whitespace-pre">{label}</p>
        </div>
      </div>
      <div className="bg-white relative rounded-[10px] shrink-0 w-full">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="box-border content-stretch flex gap-[8px] items-center overflow-clip pl-[12px] pr-[10px] py-[10px] relative w-full hover:bg-[#f6f8fa] transition-colors cursor-pointer"
        >
          <div className="basis-0 font-['Inter:Regular',_sans-serif] font-normal grow leading-[0] min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-[#868c98] text-[14px] text-nowrap tracking-[-0.084px]">
            <p className="[white-space-collapse:collapse] leading-[20px] overflow-ellipsis overflow-hidden">
              {displayValue}
            </p>
          </div>
          <div className="overflow-clip relative shrink-0 size-[20px]">
            <div className={`absolute inset-[35.83%_26.14%_35%_26.14%] transition-transform ${isOpen ? 'rotate-180' : ''}`}>
              <svg className="block max-w-none size-full" fill="none" viewBox="0 0 20 20">
                <path d="M10.5001 12.4144L15.2072 7.70733L16.2678 8.76794L10.5001 14.5356L4.73242 8.76794L5.79303 7.70733L10.5001 12.4144Z" fill="#525866"/>
              </svg>
            </div>
          </div>
        </button>
        <div aria-hidden="true" className="absolute border border-[#e2e4e9] border-solid inset-0 pointer-events-none rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)]" />
        
        {isOpen && (
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 bg-[#ffffff] rounded-[10px] border border-[#e2e4e9] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] z-[9999] p-2 max-h-48 overflow-y-auto text-[14px] top-full mt-2">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={`w-full text-left px-3 py-2 rounded-[8px] hover:bg-[#f6f8fa] transition-colors whitespace-nowrap ${
                    value === option.value ? 'text-[#e16349]' : 'text-[#525866]'
                  }`}
                  style={value === option.value ? { backgroundColor: 'rgba(225, 99, 73, 0.1)' } : undefined}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// Custom Radio Component
const CustomRadio: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, onChange, label }) => {
  return (
    <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
      <div className="overflow-clip relative shrink-0 size-[20px]">
        <button
          onClick={onChange}
          className="relative size-full"
        >
          <div className="absolute bg-[#e2e4e9] inset-[10%] rounded-[96px]" />
          <div className="absolute bg-white inset-[17.5%] rounded-[96px] shadow-[0px_2px_2px_0px_rgba(27,28,29,0.12)]" />
          {checked && (
            <div className="absolute bg-[#375dfb] inset-[25%] rounded-[96px]" />
          )}
        </button>
      </div>
      <div className="font-['SF_Pro:Regular',_sans-serif] font-normal leading-[0] relative shrink-0 text-[12px] text-black text-center text-nowrap">
        <p className="leading-[16px] whitespace-pre">{label}</p>
      </div>
    </div>
  );
};

interface FilterPopupProps {
  filterType: string;
  isOpen: boolean;
  onClose: () => void;
  filters: FiltersState;
  onFiltersChange: (filters: Partial<FiltersState>) => void;
  position: { top: number; left: number };
  responsesFilters?: ResponsesFiltersState;
  onResponsesFiltersChange?: (filters: Partial<ResponsesFiltersState>) => void;
}

export const FilterPopup: React.FC<FilterPopupProps> = ({
  filterType,
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  position,
  responsesFilters,
  onResponsesFiltersChange
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const currencies = useCurrencies();
  const handleDigitOnlyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedCtrl = e.ctrlKey || e.metaKey;
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowedCtrl) {
      const key = e.key.toLowerCase();
      if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
    }
    if (allowedKeys.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleAvitoExperienceChange = (
    key: 'avitoExperienceMin' | 'avitoExperienceMax',
    value: string,
  ) => {
    onFiltersChange(buildAvitoExperienceInputPatch(key, value, filters));
  };

  const handleAvitoExperiencePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    key: 'avitoExperienceMin' | 'avitoExperienceMax',
  ) => {
    e.preventDefault();
    const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
    onFiltersChange(buildAvitoExperienceInputPatch(key, text, filters));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderFilterContent = () => {
    switch (filterType) {
        case 'hh-params':
          return (
            <div className="space-y-4">
              <div>
                <div className="space-y-1">
                  {[
                    { value: 'active_search', label: 'Активно ищет работу' },
                    { value: 'looking_for_offers', label: 'Рассматривает предложения' },
                    { value: 'has_job_offer', label: 'Предложили работу, решает' },
                    { value: 'accepted_job_offer', label: 'Вышел на новое место' },
                    { value: 'not_looking_for_job', label: 'Не ищет работу' }
                  ].map(status => (
                    <CustomCheckbox
                      key={status.value}
                      checked={filters.jobSearchStatus?.includes(status.value) || false}
                      onChange={(checked) => {
                        const current = filters.jobSearchStatus || [];
                        if (checked) {
                          onFiltersChange({ jobSearchStatus: [...current, status.value] });
                        } else {
                          onFiltersChange({ jobSearchStatus: current.filter((v: string) => v !== status.value) });
                        }
                      }}
                      label={status.label}
                    />
                  ))}
                </div>
              </div>

            </div>
          );



      case 'experience':
        return (
          <div className="space-y-4">
            <div>
              <div className="space-y-3">
                <CustomSelect
                  label="Опыт в должности"
                  value={filters.positionExperience || ''}
                  onChange={(value) => onFiltersChange({ positionExperience: value })}
                  options={EXPERIENCE_OPTIONS}
                  placeholder="Выберите опыт в должности..."
                />
                <CustomSelect
                  label="Общий опыт"
                  value={filters.totalExperience || ''}
                  onChange={(value) => onFiltersChange({ totalExperience: value })}
                  options={EXPERIENCE_OPTIONS}
                  placeholder="Выберите общий опыт..."
                />
              </div>
            </div>
          </div>
        );


      case 'age':
        return (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={filters.ageFrom || ''}
                inputMode="numeric"
                pattern="\\d*"
                onKeyDown={(e) => {
                  const allowedCtrl = e.ctrlKey || e.metaKey;
                  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                  if (allowedCtrl) {
                    const key = e.key.toLowerCase();
                    if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                  }
                  if (allowedKeys.includes(e.key)) return;
                  if (!/^[0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                  const digits = text.replace(/\D/g, '');
                  onFiltersChange({ ageFrom: digits });
                }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  onFiltersChange({ ageFrom: digits });
                }}
                placeholder="От"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <input
                type="text"
                value={filters.ageTo || ''}
                inputMode="numeric"
                pattern="\\d*"
                onKeyDown={(e) => {
                  const allowedCtrl = e.ctrlKey || e.metaKey;
                  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
                  if (allowedCtrl) {
                    const key = e.key.toLowerCase();
                    if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                  }
                  if (allowedKeys.includes(e.key)) return;
                  if (!/^[0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                  const digits = text.replace(/\D/g, '');
                  onFiltersChange({ ageTo: digits });
                }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  onFiltersChange({ ageTo: digits });
                }}
                placeholder="До"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        );

      case 'avito-experience':
        return (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                pattern="\\d*"
                value={normalizeAvitoExperienceValue(filters.avitoExperienceMin)}
                onKeyDown={handleDigitOnlyKeyDown}
                onPaste={(e) => handleAvitoExperiencePaste(e, 'avitoExperienceMin')}
                onChange={(e) => handleAvitoExperienceChange('avitoExperienceMin', e.target.value)}
                placeholder="от"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="\\d*"
                value={normalizeAvitoExperienceValue(filters.avitoExperienceMax)}
                onKeyDown={handleDigitOnlyKeyDown}
                onPaste={(e) => handleAvitoExperiencePaste(e, 'avitoExperienceMax')}
                onChange={(e) => handleAvitoExperienceChange('avitoExperienceMax', e.target.value)}
                placeholder="до"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        );

      case 'employment':
        return (
          <div className="space-y-4">
            <div>
              <div className="space-y-2">
                {EMPLOYMENT_TYPE_OPTIONS.filter(o => o.value !== '').map((option) => (
                  <CustomCheckbox
                    key={option.value}
                    checked={(filters.employmentTypes || []).includes(option.value)}
                    onChange={(checked) => {
                      const current = filters.employmentTypes || [];
                      const next = checked
                        ? Array.from(new Set([...current, option.value]))
                        : current.filter((v: string) => v !== option.value);
                      onFiltersChange({ employmentTypes: next, employmentType: next.length === 1 ? next[0] : '' });
                    }}
                    label={option.label}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 'industry':
        return (
          <div className="space-y-4">
            <div>
              <div className="space-y-3">
                <CustomSelect
                  label="Отрасль"
                  value={filters.industry || ''}
                  onChange={(value) => onFiltersChange({ industry: value })}
                  options={INDUSTRY_OPTIONS}
                  placeholder="Выберите отрасль..."
                />
                <CustomSelect
                  label="Специализация"
                  value={filters.specialization || ''}
                  onChange={(value) => onFiltersChange({ specialization: value })}
                  options={SPECIALIZATION_OPTIONS}
                  placeholder="Выберите специализацию..."
                />
                <CustomSelect
                  label="Период опыта в отрасли"
                  value={filters.experiencePeriod || ''}
                  onChange={(value) => onFiltersChange({ experiencePeriod: value })}
                  options={EXPERIENCE_OPTIONS}
                  placeholder="Выберите период опыта в отрасли..."
                />
              </div>
            </div>
          </div>
        );

      case 'salary':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-gray-700">Валюта:</span>
              <select
                value={filters.salaryCurrency || 'RUR'}
                onChange={(e) => onFiltersChange({ salaryCurrency: e.target.value })}
                className="px-2 py-1 border border-gray-300 rounded-lg text-sm bg-white outline-none"
              >
                {currencies.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={formatNumberWithSpaces(filters.salaryFrom)}
                inputMode="numeric"
                pattern="\\d*"
                onKeyDown={(e) => {
                  const allowedCtrl = e.ctrlKey || e.metaKey;
                  const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                  if (allowedCtrl) {
                    const key = e.key.toLowerCase();
                    if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                  }
                  if (allowedKeys.includes(e.key)) return;
                  if (!/^[0-9\s]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                  const digits = text.replace(/\D/g, '');
                  onFiltersChange({ salaryFrom: digits });
                }}
                onChange={(e) => {
                  const raw = parseFormattedNumber(e.target.value);
                  if (raw === '') { onFiltersChange({ salaryFrom: '' }); return; }
                  const num = parseInt(raw, 10);
                  if (Number.isNaN(num) || num < 0) { onFiltersChange({ salaryFrom: '' }); return; }
                  onFiltersChange({ salaryFrom: String(num) });
                }}
                placeholder="От"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <input
                type="text"
                value={formatNumberWithSpaces(filters.salaryTo)}
                inputMode="numeric"
                pattern="\\d*"
                onKeyDown={(e) => {
                  const allowedCtrl = e.ctrlKey || e.metaKey;
                  const allowedKeys = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                  if (allowedCtrl) {
                    const key = e.key.toLowerCase();
                    if (key === 'a' || key === 'c' || key === 'v' || key === 'x') return;
                  }
                  if (allowedKeys.includes(e.key)) return;
                  if (!/^[0-9\s]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = (e.clipboardData || (window as any).clipboardData)?.getData('text') || '';
                  const digits = text.replace(/\D/g, '');
                  onFiltersChange({ salaryTo: digits });
                }}
                onChange={(e) => {
                  const raw = parseFormattedNumber(e.target.value);
                  if (raw === '') { onFiltersChange({ salaryTo: '' }); return; }
                  const num = parseInt(raw, 10);
                  if (Number.isNaN(num) || num < 0) { onFiltersChange({ salaryTo: '' }); return; }
                  onFiltersChange({ salaryTo: String(num) });
                }}
                placeholder="До"
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            <div className="mt-3">
              <CustomCheckbox
                checked={filters.incomeSpecified || false}
                onChange={(checked) => onFiltersChange({ incomeSpecified: checked, hasSalary: checked })}
                label="Указана ЗП"
              />
            </div>
          </div>
        );

      case 'geography':
        return (
          <div className="space-y-4">
            <div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Города</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {cities.slice(0, 6).map((city) => (
                      <CustomCheckbox
                        key={city}
                        checked={filters.cities.includes(city)}
                        onChange={(checked) => {
                          const next = checked ? [...filters.cities, city] : filters.cities.filter((c: string) => c !== city);
                          onFiltersChange({ cities: next });
                        }}
                        label={city}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Возраст</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="number"
                      value={filters.ageFrom || ''}
                      onChange={(e) => onFiltersChange({ ageFrom: e.target.value })}
                      placeholder="от"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349]"
                    />
                    <input
                      type="number"
                      value={filters.ageTo || ''}
                      onChange={(e) => onFiltersChange({ ageTo: e.target.value })}
                      placeholder="до"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349]"
                    />
                  </div>
                  <CustomCheckbox
                    checked={filters.includeNoAge}
                    onChange={(checked) => onFiltersChange({ includeNoAge: checked })}
                    label="Включая без возраста"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Пол</label>
                  <div className="flex gap-4">
                    <CustomRadio
                      checked={!filters.gender}
                      onChange={() => onFiltersChange({ gender: '' })}
                      label="Любой"
                    />
                    <CustomRadio
                      checked={filters.gender === 'M'}
                      onChange={() => onFiltersChange({ gender: 'M' })}
                      label="М"
                    />
                    <CustomRadio
                      checked={filters.gender === 'F'}
                      onChange={() => onFiltersChange({ gender: 'F' })}
                      label="Ж"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );


      case 'responses-experience':
        return (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-3">Опыт работы</p>
            {RESPONSES_EXPERIENCE_OPTIONS.map(option => (
              <CustomCheckbox
                key={option.value}
                checked={responsesFilters?.experience?.includes(option.value) || false}
                onChange={(checked) => {
                  if (!onResponsesFiltersChange || !responsesFilters) return;
                  const newExperience = checked
                    ? [...(responsesFilters.experience || []), option.value]
                    : (responsesFilters.experience || []).filter(e => e !== option.value);
                  onResponsesFiltersChange({ experience: newExperience });
                }}
                label={option.label}
              />
            ))}
          </div>
        );

      case 'responses-age':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Возраст</p>
            <div className="flex gap-2 items-center">
              <input
                type="number"
                placeholder="От"
                value={responsesFilters?.ageFrom || ''}
                onChange={(e) => onResponsesFiltersChange?.({ ageFrom: e.target.value })}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#E16349]"
              />
              <span className="text-gray-500">—</span>
              <input
                type="number"
                placeholder="До"
                value={responsesFilters?.ageTo || ''}
                onChange={(e) => onResponsesFiltersChange?.({ ageTo: e.target.value })}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#E16349]"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: '18-25', from: '18', to: '25' },
                { label: '25-35', from: '25', to: '35' },
                { label: '35-45', from: '35', to: '45' },
                { label: '45+', from: '45', to: '' }
              ].map(preset => (
                <button
                  key={preset.label}
                  onClick={() => {
                    onResponsesFiltersChange?.({ ageFrom: preset.from, ageTo: preset.to });
                  }}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 'responses-salary':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Зарплата</p>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={responsesFilters?.salaryFrom ? formatNumberWithSpaces(responsesFilters.salaryFrom) : ''}
                inputMode="numeric"
                pattern="\\d*"
                placeholder="От"
                onChange={(e) => {
                  const raw = parseFormattedNumber(e.target.value);
                  if (raw === '') { onResponsesFiltersChange?.({ salaryFrom: '' }); return; }
                  const num = parseInt(raw, 10);
                  if (Number.isNaN(num) || num < 0) { onResponsesFiltersChange?.({ salaryFrom: '' }); return; }
                  onResponsesFiltersChange?.({ salaryFrom: String(num) });
                }}
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="text-gray-500">—</span>
              <input
                type="text"
                value={responsesFilters?.salaryTo ? formatNumberWithSpaces(responsesFilters.salaryTo) : ''}
                inputMode="numeric"
                pattern="\\d*"
                placeholder="До"
                onChange={(e) => {
                  const raw = parseFormattedNumber(e.target.value);
                  if (raw === '') { onResponsesFiltersChange?.({ salaryTo: '' }); return; }
                  const num = parseInt(raw, 10);
                  if (Number.isNaN(num) || num < 0) { onResponsesFiltersChange?.({ salaryTo: '' }); return; }
                  onResponsesFiltersChange?.({ salaryTo: String(num) });
                }}
                className="w-28 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        );

      case 'responses-location':
        return (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Регион</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {cities.map((city) => (
                <CustomCheckbox
                  key={city}
                  checked={responsesFilters?.areaIds?.includes(city) || false}
                  onChange={(checked) => {
                    if (!onResponsesFiltersChange || !responsesFilters) return;
                    const next = checked 
                      ? [...(responsesFilters.areaIds || []), city] 
                      : (responsesFilters.areaIds || []).filter(c => c !== city);
                    onResponsesFiltersChange({ areaIds: next });
                  }}
                  label={city}
                />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      
      {/* Popup */}
      <div
        ref={popupRef}
        className="fixed bg-white rounded-xl shadow-lg border border-gray-200 z-[9999] p-3 w-fit"
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        {/* Content */}
        <div className="px-1 pt-1">
          {renderFilterContent()}
        </div>

        {/* Footer with Save button */}
        <div className="px-1 pt-3 pb-1">
          <button
            onClick={onClose}
            className="w-60 px-4 py-2 bg-[#e16349] text-white rounded-lg hover:bg-[#d14d3a] transition-colors font-medium"
          >
            Сохранить
          </button>
        </div>
      </div>
    </>
  );
};

export default FilterPopup;
